import { placeRegion, type PlaceOption } from "./places";
import type { Workspace, WorkspaceAction } from "./types";
import { cityKey } from "./workspace";

/** What swapping a compared city for a picked place does: the edits to apply, or why it can't. */
export type DestinationReplacement = { actions: WorkspaceAction[] } | { duplicate: true };

/**
 * The edits that swap a compared city for a picked place. A pick with the same name but another
 * region (Athens, Greece to Athens, Georgia) re-adds the city with the new region and airport.
 * @param workspace - The current workspace.
 * @param city - The compared city being replaced.
 * @param place - The place picked in its stead.
 * @returns The edits, none when the pick is the same place, or `duplicate` when the pick is
 * another city already in the comparison.
 * @throws Never.
 */
export const replaceDestination = (
  workspace: Pick<Workspace, "destinations" | "places">,
  city: string,
  place: PlaceOption
): DestinationReplacement => {
  const key = cityKey(place.city);
  const region = placeRegion(place);
  const sameName = cityKey(city) === key;
  if (sameName && workspace.places?.[key] === region) return { actions: [] };
  // Swapping in a city that's already compared would just drop this one.
  if (!sameName && workspace.destinations.some((other) => cityKey(other) === key)) return { duplicate: true };
  const code = place.codes[0] ?? null;
  return {
    actions: [
      { type: "removeDestination", city },
      { type: "addDestination", city: place.city, region },
      // A same-name swap keeps the city's key, so clear the old place's airport when the new one has none.
      ...(code || sameName ? [{ type: "setAirport", city: place.city, code } as const] : []),
    ],
  };
};
